import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Star, MapPin } from "lucide-react";
import PromoStrip from "@/components/site/PromoStrip";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const TODAY = new Date().toISOString().slice(0, 10);
const PLUS = (d) => new Date(Date.now() + d * 86400000).toISOString().slice(0, 10);

export default function Hotels() {
  const { format: fmt } = useCurrency();
  const [form, setForm] = useState({ city: "Dubai", check_in: PLUS(7), check_out: PLUS(10), guests: 2, rooms: 1 });
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const search = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/hotels/search", form);
      setHotels(data.hotels);
    } catch (_e) { toast.error("Search failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { search(); /* initial */ // eslint-disable-next-line
  }, []);

  const nights = Math.max(1, Math.round((new Date(form.check_out) - new Date(form.check_in)) / 86400000));

  const book = async (h) => {
    if (!user) { toast.error("Please sign in to continue"); return; }
    const total = h.price * nights * form.rooms;
    const { data } = await api.post("/bookings", {
      booking_type: "hotel", item: { ...h, nights, rooms: form.rooms, check_in: form.check_in, check_out: form.check_out },
      travelers: [{ name: user.name }], total_amount: total, currency: "USD",
      contact: { email: user.email, name: user.name, phone: "" },
    });
    navigate(`/checkout/${data.id}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-primary-dark)] rounded-3xl p-6 md:p-8 text-white">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold">Stay where stories begin</h1>
        <form onSubmit={search} className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3 bg-white/95 p-4 rounded-2xl text-slate-700">
          <div className="md:col-span-4">
            <Label>City</Label>
            <Input data-testid="hotel-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required className="h-11" />
          </div>
          <div className="md:col-span-2">
            <Label>Check-in</Label>
            <Input data-testid="hotel-checkin" type="date" min={TODAY} value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} className="h-11" />
          </div>
          <div className="md:col-span-2">
            <Label>Check-out</Label>
            <Input data-testid="hotel-checkout" type="date" min={form.check_in} value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} className="h-11" />
          </div>
          <div className="md:col-span-1">
            <Label>Guests</Label>
            <Input data-testid="hotel-guests" type="number" min={1} value={form.guests} onChange={(e) => setForm({ ...form, guests: Number(e.target.value) })} className="h-11" />
          </div>
          <div className="md:col-span-1">
            <Label>Rooms</Label>
            <Input data-testid="hotel-rooms" type="number" min={1} value={form.rooms} onChange={(e) => setForm({ ...form, rooms: Number(e.target.value) })} className="h-11" />
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button data-testid="hotel-search-submit" type="submit" className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] h-11 rounded-full">Search</Button>
          </div>
        </form>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 stagger">
        {loading && <div className="md:col-span-3 text-center py-8 text-slate-500">Searching...</div>}
        {!loading && hotels.map((h) => (
          <Card key={h.id} data-testid={`hotel-card-${h.id}`} className="overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all">
            <div className="h-44 bg-slate-100">
              <img src={h.image} alt={h.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-lg font-semibold text-[var(--brand-accent)]">{h.name}</h3>
                <div className="flex items-center gap-1 text-amber-500 text-sm">
                  {Array.from({ length: h.rating }, (_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 stroke-amber-400" />)}
                </div>
              </div>
              <div className="flex items-center text-sm text-slate-500 mt-1">
                <MapPin className="w-3.5 h-3.5 mr-1" /> {h.city}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {h.amenities.map((a) => <span key={a} className="text-[11px] px-2 py-1 rounded-full bg-teal-50 text-teal-700">{a}</span>)}
              </div>
              <div className="flex items-end justify-between mt-4">
                <div>
                  <div className="font-heading text-2xl font-bold text-[var(--brand-accent)]">{fmt(h.price, { decimals: 0 })}</div>
                  <div className="text-xs text-slate-500">per night · {nights} night{nights > 1 ? "s" : ""}</div>
                </div>
                <Button data-testid={`hotel-book-${h.id}`} onClick={() => book(h)} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">Book</Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <PromoStrip />
    </div>
  );
}
